// src/components/settings/PetTypesTab.tsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Alert, Table, TableRow } from '@mui/material';
import { referenceDataAPI, PetType } from '../../services/api';
import { useErrorHandler } from '../../errors';
import { useGlobalToast } from '../../App';
import {
  ConfirmDeleteDialog,
  RefCountChip,
  SystemChip,
} from './shared/SettingsElements';
import SettingsHeaderRow from './shared/SettingsHeaderRow';
import LoadingSpinner from '../common/LoadingSpinner';
import SettingsTableActions from './shared/SettingsTableActions';
import SimilarityDialog from '../forms/shared/SimilarityDialog';
import { checkReferenceDataSimilarity } from '../../utils/referenceDataSimilarityHelper';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableHeaderCell,
  StyledTableBody,
  StyledTableRow,
  StyledTableCell,
  StyledTableChipCell,
  StyledTableChipCellContent,
  StyledTableActionsCell,
} from './shared/SettingsTableElements';
import PetTypeDialog from './shared/dialogs/PetTypeDialog';

interface PetTypesTabProps {
  usage: Record<number, number>;
  onDataChanged: () => void;
}

const PetTypesTab: React.FC<PetTypesTabProps> = ({ usage, onDataChanged }) => {
  const [items, setItems] = useState<PetType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PetType | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [searchText, setSearchText] = useState('');
  const [showSimilarityDialog, setShowSimilarityDialog] = useState(false);
  const [similarItem, setSimilarItem] = useState<PetType | null>(null);
  const [pendingSave, setPendingSave] = useState<{
    type: string;
    active: boolean;
  } | null>(null);
  const { handleError } = useErrorHandler();
  const { showSuccess, showError } = useGlobalToast();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setItems(await referenceDataAPI.getPetTypes());
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    if (!searchText) return items;
    const lower = searchText.toLowerCase();
    return items.filter((i) => i.type.toLowerCase().includes(lower));
  }, [items, searchText]);

  const openDialog = (item?: PetType) => {
    setEditing(item ?? null);
    setDialogOpen(true);
  };

  const proceedWithSave = async (formData: {
    type: string;
    active: boolean;
  }) => {
    try {
      if (editing) {
        await referenceDataAPI.updatePetType(editing.id, formData);
        showSuccess();
      } else {
        await referenceDataAPI.createPetType(formData);
        showSuccess();
      }
      await fetchData();
      onDataChanged();
      setDialogOpen(false);
    } catch (e) {
      handleError(e);
    }
  };

  const save = async (formData: { type: string; active: boolean }) => {
    const { isExactMatch, match } = checkReferenceDataSimilarity(
      formData.type,
      items,
      (i) => i.type,
      (i) => i.id,
      editing?.id
    );

    if (isExactMatch) {
      showError('A pet type with this name already exists');
      return;
    }

    if (match) {
      setSimilarItem(match);
      setPendingSave(formData);
      setShowSimilarityDialog(true);
      return;
    }

    await proceedWithSave(formData);
  };

  const handleUseExisting = () => {
    setShowSimilarityDialog(false);
    setSimilarItem(null);
    setPendingSave(null);
    setDialogOpen(false);
  };

  const handleCreateAnyway = async () => {
    setShowSimilarityDialog(false);
    setSimilarItem(null);
    if (pendingSave) {
      await proceedWithSave(pendingSave);
    }
    setPendingSave(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await referenceDataAPI.deletePetType(deleteTarget.id);
      showSuccess();
      await fetchData();
      onDataChanged();
    } catch (e) {
      handleError(e);
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading)
    return <LoadingSpinner message="Loading Pet Types..." size={80} />;

  return (
    <Box>
      <SettingsHeaderRow
        title="Pet Types"
        onAdd={() => openDialog()}
        addLabel="Add Pet Type"
        buttonMinWidth="266px"
        onSearch={setSearchText}
        onClearSearch={() => setSearchText('')}
        searchText={searchText}
        searchPlaceholder="Search pet types..."
      />
      {items.length === 0 ? (
        <Alert severity="info">No pet types found.</Alert>
      ) : filtered.length === 0 ? (
        <Alert severity="info">No pet types match "{searchText}".</Alert>
      ) : (
        <StyledTableContainer>
          <Table size="small">
            <StyledTableHead>
              <TableRow>
                <StyledTableHeaderCell>Type</StyledTableHeaderCell>
                <StyledTableHeaderCell align="center">
                  Refs
                </StyledTableHeaderCell>
                <StyledTableHeaderCell align="right">
                  Actions
                </StyledTableHeaderCell>
              </TableRow>
            </StyledTableHead>
            <StyledTableBody>
              {filtered.map((item, index) => {
                const refCount = usage[item.id] ?? 0;
                return (
                  <StyledTableRow key={item.id} isOdd={index % 2 === 0}>
                    <StyledTableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography>{item.type}</Typography>
                        {item.isSystem && <SystemChip />}
                      </Box>
                    </StyledTableCell>
                    <StyledTableChipCell>
                      <StyledTableChipCellContent>
                        <RefCountChip count={refCount} />
                      </StyledTableChipCellContent>
                    </StyledTableChipCell>
                    <StyledTableActionsCell align="right">
                      <SettingsTableActions
                        itemId={item.id}
                        itemName={item.type}
                        refCount={refCount}
                        isSystem={item.isSystem}
                        onEdit={() => openDialog(item)}
                        onDelete={() =>
                          setDeleteTarget({ id: item.id, name: item.type })
                        }
                        refLabel="reference"
                      />
                    </StyledTableActionsCell>
                  </StyledTableRow>
                );
              })}
            </StyledTableBody>
          </Table>
        </StyledTableContainer>
      )}

      <PetTypeDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={save}
        editing={editing}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        title="Delete pet type?"
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <SimilarityDialog
        open={showSimilarityDialog}
        entityType="petType"
        similarEntity={similarItem}
        isEditMode={!!editing}
        onUseExisting={handleUseExisting}
        onCreateNew={handleCreateAnyway}
        onClose={() => setShowSimilarityDialog(false)}
      />
    </Box>
  );
};

export default PetTypesTab;
